// ============================================================
// ASSET FILES - surface and material palettes you can carry out.
//
// A map export already carries its palettes inside the terrain, but it
// carries a whole map with them. This is the palette alone: the set of
// surfaces and materials a GM curates once and wants in every map they
// build. One format for both palettes, either half optional - a second
// file kind would be a second thing to version, sniff and get wrong
// (the mapFile argument, verbatim).
//
// Pure. Zero React dependency.
// ============================================================
import type { SurfaceDef } from './zones';
import type { Material } from './voxels';

/** What every asset file says it is. Anything else is somebody else's. */
export const ASSET_FILE_KIND = 'deathwatch-assets';

const ASSET_FILE_VERSION = 1;

export interface AssetFile {
  kind: typeof ASSET_FILE_KIND;
  version: number;
  exportedAt?: string;
  surfaces?: Record<string, SurfaceDef>;
  materials?: Record<string, Material>;
}

/**
 * Palettes → the file to hand somebody. Empty halves are omitted, so a
 * surfaces-only export does not read as "and delete your materials".
 * `exportedAt` is passed in: this module is pure and does not read the
 * clock (the mapFile precedent).
 */
export function toAssetFile(
  surfaces: Record<string, SurfaceDef>,
  materials: Record<string, Material>,
  exportedAt?: string,
): AssetFile {
  return {
    kind: ASSET_FILE_KIND,
    version: ASSET_FILE_VERSION,
    ...(exportedAt ? { exportedAt } : {}),
    ...(Object.keys(surfaces).length ? { surfaces } : {}),
    ...(Object.keys(materials).length ? { materials } : {}),
  };
}

export interface AssetReadResult {
  surfaces: Record<string, SurfaceDef>;
  materials: Record<string, Material>;
  /** Why nothing could be read, in words a person can act on. */
  error: string | null;
}

/** An entry is usable if it is an object with the naming field present. */
function usableEntries<T extends { key: string }>(record: unknown, nameField: 'label' | 'name'): Record<string, T> {
  if (!record || typeof record !== 'object') return {};
  const out: Record<string, T> = {};
  for (const [key, value] of Object.entries(record as Record<string, unknown>)) {
    // The record key is the identity everywhere downstream; a mismatched
    // inner key would make two names for one surface.
    if (isUsable(value, nameField)) out[key] = { ...(value as unknown as T), key };
  }
  return out;
}

/** An entry with a name and a colour - the least an asset needs to be shown. */
function isUsable(value: unknown, nameField: 'label' | 'name'): value is Record<string, unknown> {
  if (!value || typeof value !== 'object') return false;
  const entry = value as Record<string, unknown>;
  return typeof entry[nameField] === 'string' && typeof entry.colour === 'string';
}

/**
 * A parsed file → the palettes to merge, or a reason it cannot be.
 *
 * Never throws, and unreadable ENTRIES are dropped rather than sinking
 * the file - a curated palette with one corrupt row should still import
 * the other nineteen.
 */
export function readAssetFile(data: unknown): AssetReadResult {
  const none = { surfaces: {}, materials: {} };
  if (!data || typeof data !== 'object') {
    return { ...none, error: 'That file does not contain assets.' };
  }
  const file = data as Partial<AssetFile>;
  if (file.kind !== ASSET_FILE_KIND) {
    return {
      ...none,
      error: 'That is not a Deathwatch asset file. A map goes through the map library instead.',
    };
  }
  const surfaces = usableEntries<SurfaceDef>(file.surfaces, 'label');
  const materials = usableEntries<Material>(file.materials, 'name');
  if (!Object.keys(surfaces).length && !Object.keys(materials).length) {
    return { ...none, error: 'That asset file has nothing readable in it.' };
  }
  return { surfaces, materials, error: null };
}
